import type {
  CampaignTemplate,
  MarketingStep,
  TemplatesResponse,
} from "./types";
import { emailThenSmsSteps, withStudioScenario } from "./studioTemplates";

/** Дополнительные задачи используют только условия и каналы существующего движка.
 * Каталог меняет новые черновики; сохранённые кампании из БД сюда не попадают. */
export function extendStudioCatalog(
  catalog: TemplatesResponse,
): TemplatesResponse {
  const base = catalog.campaigns.find((t) => t.id === "early-return-21");
  if (!base) return catalog;
  const rules = (
    orders: number,
    from: number,
    to: number,
  ): CampaignTemplate["audience"] => ({
    kind: "group",
    op: "all",
    children: [
      { kind: "rule", field: "ordersCount", op: "gte", value: orders },
      { kind: "rule", field: "daysSinceLastOrder", op: "gte", value: from },
      { kind: "rule", field: "daysSinceLastOrder", op: "lte", value: to },
    ],
  });
  const recipes: {
    id: string;
    name: string;
    goal: string;
    description: string;
    orders: number;
    from: number;
    to: number;
    mode?: "once";
    channel?: "sms" | "push" | "email";
    ab?: boolean;
    emailThenSms?: boolean;
  }[] = [
    {
      id: "return-14",
      name: "Вернуть через две недели",
      goal: "Раннее возвращение",
      description:
        "Повторные гости с паузой 14–20 дней. Каскад каналов с проверкой доступности.",
      orders: 2,
      from: 14,
      to: 20,
    },
    {
      id: "return-60",
      name: "Вернуть через два месяца",
      goal: "Возвращение",
      description:
        "Давние гости без покупок от 60 до 89 дней. Отдельная аудитория для мягкого приглашения.",
      orders: 2,
      from: 60,
      to: 89,
    },
    {
      id: "return-180",
      name: "Встретиться после полугода",
      goal: "Возвращение",
      description:
        "Редкое приглашение после 180–365 дней без заказа. Проверьте аудиторию перед запуском.",
      orders: 2,
      from: 180,
      to: 365,
    },
    {
      id: "third-order",
      name: "Пригласить на третий заказ",
      goal: "Повторная покупка",
      description:
        "Ровно два заказа и пауза от недели до месяца. Закрепляем знакомство с меню.",
      orders: 2,
      from: 7,
      to: 30,
    },
    {
      id: "regular-week",
      name: "Напомнить через неделю",
      goal: "Удержание",
      description:
        "Не менее четырёх заказов и пауза 7–13 дней. Для гостей с привычкой заказывать регулярно.",
      orders: 4,
      from: 7,
      to: 13,
    },
    {
      id: "loyal-12",
      name: "Личное приглашение любимым гостям",
      goal: "Лояльность",
      description:
        "Гости с двенадцатью заказами и более, которые не возвращались 14–44 дня.",
      orders: 12,
      from: 14,
      to: 44,
    },
    {
      id: "menu-week",
      name: "Знакомство с другим вкусом",
      goal: "Знакомство с меню",
      description:
        "Разовая кампания для гостей с паузой 7–20 дней. Содержание блюда выбираете вы.",
      orders: 2,
      from: 7,
      to: 20,
      mode: "once",
    },
    {
      id: "weekend",
      name: "Приглашение к выходным",
      goal: "Повод для заказа",
      description:
        "Разовый запуск по выбранной дате. Установите день и местное время в настройках события.",
      orders: 1,
      from: 7,
      to: 44,
      mode: "once",
    },
    {
      id: "evening",
      name: "Идея для вечера",
      goal: "Повод для заказа",
      description:
        "Один пуш гостям с историей заказов. Время отправки и тип уведомления задаются перед запуском.",
      orders: 1,
      from: 7,
      to: 30,
      mode: "once",
      channel: "push",
    },
    {
      id: "push-return",
      name: "Вернуть бесплатным пушем",
      goal: "Возвращение",
      description:
        "Только пуш приложения: без переходов в платные каналы. Пауза 21–44 дня.",
      orders: 2,
      from: 21,
      to: 44,
      channel: "push",
    },
    {
      id: "email-return",
      name: "Письмо с приглашением",
      goal: "Возвращение",
      description:
        "Одно редактируемое письмо после 30–60 дней без заказа. Перед отправкой проверяется контакт и согласие.",
      orders: 2,
      from: 30,
      to: 60,
      channel: "email",
    },
    {
      id: "email-then-sms",
      name: "Письмо, а кто не открыл — SMS",
      goal: "Возвращение",
      description:
        "Сначала письмо. Кто не открыл его за сутки и не заказал — получает SMS. Гостям без email — сразу SMS. Пауза 30–60 дней без заказа.",
      orders: 2,
      from: 30,
      to: 60,
      emailThenSms: true,
    },
    {
      id: "sms-return",
      name: "Короткое приглашение по SMS",
      goal: "Возвращение",
      description:
        "Одна SMS после 30–60 дней без заказа. Расходы считаются по действующему тарифу.",
      orders: 2,
      from: 30,
      to: 60,
      channel: "sms",
    },
    {
      id: "ab-push",
      name: "A/B-тест двух приглашений",
      goal: "Сравнение сообщений",
      description:
        "Аудитория случайно делится 50/50. Каждый гость получает только один вариант пуша.",
      orders: 2,
      from: 21,
      to: 60,
      ab: true,
    },
  ];
  const extra = recipes.map((recipe) => {
    const template = structuredClone(base);
    template.id = `studio-${recipe.id}`;
    template.name = recipe.name;
    template.goal = recipe.goal;
    template.description = recipe.description;
    template.audience = rules(recipe.orders, recipe.from, recipe.to);
    template.mode = recipe.mode || "automatic";
    template.availability = {
      available: true,
      requiredFields: ["ordersCount", "daysSinceLastOrder"],
      requires: [],
    };
    if (recipe.id === "third-order")
      template.audience.children[0] = {
        kind: "rule",
        field: "ordersCount",
        op: "eq",
        value: 2,
      };
    if (recipe.id === "return-180") template.reentryDays = 90;
    if (recipe.channel)
      template.defaultSteps = template.defaultSteps
        ?.filter((s) => s.channel === recipe.channel)
        .map((s) => ({
          ...s,
          nextStepId: undefined,
          unreadFallback: undefined,
          onUnavailable: "",
          waitHours: 24,
          fallback: "no_order",
        }));
    if (recipe.emailThenSms) {
      const email = template.defaultSteps?.find((s) => s.channel === "email");
      const sms = template.defaultSteps?.find((s) => s.channel === "sms");
      if (email && sms) template.defaultSteps = emailThenSmsSteps(email, sms);
    }
    if (recipe.ab) {
      const push = template.defaultSteps?.find((s) => s.channel === "push");
      if (push?.push) {
        const exit: MarketingStep = {
          ...structuredClone(push),
          id: "ab-exit",
          type: "exit",
          unreadFallback: undefined,
          nextStepId: undefined,
        };
        template.defaultSteps = [
          {
            ...structuredClone(exit),
            id: "ab-split",
            type: "randomCohort",
            cohorts: [
              { percent: 50, stepId: "ab-a" },
              { percent: 50, stepId: "ab-b" },
            ],
          },
          {
            ...structuredClone(push),
            id: "ab-a",
            type: "message",
            nextStepId: "ab-exit",
            unreadFallback: undefined,
            push: { ...push.push, title: "Как насчёт роллов сегодня? 🍣" },
          },
          {
            ...structuredClone(push),
            id: "ab-b",
            type: "message",
            nextStepId: "ab-exit",
            unreadFallback: undefined,
            push: { ...push.push, title: "У вечера есть вкусный план ✨" },
          },
          exit,
        ];
      }
    }
    return template;
  });
  return {
    ...catalog,
    campaigns: [
      ...catalog.campaigns,
      ...extra.filter(
        (t) => !catalog.campaigns.some((original) => original.id === t.id),
      ),
    ].map(withStudioScenario),
  };
}
